import React, { useRef } from "react";
import { Film, Loader2 } from "lucide-react";
import { assetUrl } from "../../lib/api";
import { useSolo } from "../../hooks/useVideoStudio";
import { Frame } from "./Frame";
import { PreviewPlayer } from "./PreviewPlayer";

const FinalVideo = ({ video }) => {
  const ref = useRef(null);
  const claim = useSolo("final", () => ref.current?.pause());
  return (
    <Frame aspect={video.aspect}>
      <video ref={ref} key={video.export.url} src={assetUrl(video.export.url)} controls playsInline onPlay={claim} className="w-full h-full object-contain" data-testid="video-player-video" />
    </Frame>
  );
};

const Tab = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-7 px-3 rounded-full text-[11px] font-medium transition-colors ${active ? "bg-white text-slate-900" : "text-slate-400 hover:text-white"}`}>{children}</button>
);

const Empty = ({ making }) => (
  <div className="text-center text-slate-400 text-xs px-4" data-testid="video-player-empty">
    {making ? <><Loader2 className="animate-spin mx-auto mb-2 text-emerald-400" size={22} />Your clip is being made — usually 1–3 minutes.<br />You can leave this page; we'll keep going.</>
      : <><Film className="mx-auto mb-2" size={22} />Add a clip to start.</>}
  </div>
);

export const VideoPlayer = ({ video, captions, mode, setMode, jump, onClip }) => {
  const ex = video.export || {};
  const hasClips = video.clips.some((c) => c.status === "ready");
  const hasFinal = ex.status === "ready" && !!ex.url;
  const m = (mode === "export" && hasFinal) || (!hasClips && hasFinal) ? "export" : hasClips ? "preview" : null;

  return (
    <div className="rounded-2xl bg-slate-900 p-3 sm:p-4" data-testid="video-player">
      {hasClips && hasFinal && (
        <div className="mb-3 flex gap-1" data-testid="player-mode-tabs">
          <Tab active={m === "preview"} onClick={() => setMode("preview")} testid="player-tab-preview">Preview</Tab>
          <Tab active={m === "export"} onClick={() => setMode("export")} testid="player-tab-final">Finished video</Tab>
        </div>
      )}
      {m === "preview" && <PreviewPlayer video={video} captions={captions} jump={jump} onClip={onClip} />}
      {m === "export" && <FinalVideo video={video} />}
      {!m && <Frame aspect={video.aspect}><Empty making={video.clips.some((c) => c.status === "generating")} /></Frame>}
      {m && (
        <p className="mt-2 text-[11px] text-slate-400 text-center" data-testid="video-player-label">
          {m === "preview" ? "Live preview with your captions and music — tap Make video for the final file"
            : `Finished video${ex.stale ? " · made before your latest changes" : ""}`}
        </p>
      )}
    </div>
  );
};

export default VideoPlayer;
